import React, {useState, useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import produce from 'immer';
import SMCModal from '../../components/core/SMCModal';
import {PortalSettingsGetSchema} from '../../api/DBSchema/PortalSettings';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCButton from '../../components/Simple/SMCButton';
import SMCLabel from '../../components/Simple/SMCLabel';
import RowLayout from '../../components/Layouts/RowLayout';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {setProperty, hasValue} from '../../utils/utils';
import {SMCCheckbox} from '../../components/Simple/SMCCheckbox';
import tooltipIcon from '../../assets/images/help_normal.png';
import {Tooltip} from '@mui/material';
import Api from '../../api/SMPFetchHelper';
import Sepperator from '../../components/Simple/Sepperator';
import cpReportLogo from '../../assets/images/reports/cp-report-logo.png';
import quantumSparkLogo from '../../assets/images/reports/quantum-spark.png';

import {useSMCErrorHandler} from '../../utils/errorUtils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

const customDetailsSchema = PortalSettingsGetSchema.pages.notificationGatewaysReports.companyDetailsSchema;

const CUSTOM_PRIMARY_LOGO_NAME = 'logoOne';
const CUSTOM_SECONDARY_LOGO_NAME = 'logoTwo';
const DEFAULT_COPYRIGHT_TEXT = "CheckPoint Technologies Ltd.";


function CompanyDetailsEditorModal({
    isOpen,
    onRequestClose,
    onError,
    onSuccess,
    defaultObject
}) {
    const { t } = useTranslation();
    const [editorObject, setEditorObject] = useState(defaultObject);
    const { getErrorMessage } = useSMCErrorHandler();

    const setPageValue = useCallback((param, newValue) => {
        setEditorObject(editorObject => produce(editorObject, draft => (
            setProperty(draft, param, newValue)
        )))
    }, []);
    

    const enablePrimaryLogo   = editorObject[customDetailsSchema.enablePrimaryLogo];
    const enableSecondaryLogo = editorObject[customDetailsSchema.enableSecondaryLogo];
    let   customText          = editorObject[customDetailsSchema.customText];
    if (!hasValue(customText)) {
        customText = DEFAULT_COPYRIGHT_TEXT;
    }

    const [primaryLogoSrc, setPrimaryLogoSrc] = useState(cpReportLogo);
    const [secondaryLogoSrc, setSecondaryLogoSrc] = useState(quantumSparkLogo);
    const secondaryLogoWidth = 145;
    const [uploadedPrimaryFile, setUploadedPrimaryFile] = useState(null);
    const [uploadedSecondaryFile, setUploadedSecondaryFile] = useState(null);

    const [isUploading, setIsUploading] = useState(false);

    const onUploadPrimaryFile = useCallback(e => {
        // In case of cancel, don't do anything
        if (!e.target.files.length) {
            return;
        }
        setPrimaryLogoSrc(URL.createObjectURL(e.target.files[0]));
        setUploadedPrimaryFile(e.target.files[0]);
        setPageValue(customDetailsSchema.primaryLogoId, CUSTOM_PRIMARY_LOGO_NAME);
    }, [setPageValue]);

    const onUploadSecondaryFile = useCallback((e) => {
        // In case of cancel, don't do anything
        if (!e.target.files.length) {
            return;
        }
        setSecondaryLogoSrc(URL.createObjectURL(e.target.files[0]));
        setUploadedSecondaryFile(e.target.files[0]);
        setPageValue(customDetailsSchema.secondaryLogoId, CUSTOM_SECONDARY_LOGO_NAME);
    }, [setPageValue]);

    useEffect(() => {
        if (isOpen) {
            setEditorObject(defaultObject);
            const primaryLogoId = defaultObject[customDetailsSchema.primaryLogoId];
            const secondaryLogoId = defaultObject[customDetailsSchema.secondaryLogoId];
            // Primary logo
            if (primaryLogoId) {
                Api.readImageData(primaryLogoId)
                    .then(resp => {
                        setPrimaryLogoSrc(`data:image/jpeg;base64,${resp.data}`)
                    })
                    .catch(e => {
                        setPrimaryLogoSrc(null);
                    })
            } else {
                setPrimaryLogoSrc(cpReportLogo);
            }
            
            // Secondary logo
            if (secondaryLogoId) {
                Api.readImageData(secondaryLogoId)
                    .then(resp => {
                        setSecondaryLogoSrc(`data:image/jpeg;base64,${resp.data}`)
                    })
                    .catch(e => {
                        setSecondaryLogoSrc(null);
                    })
            } else {
                setSecondaryLogoSrc(quantumSparkLogo);
            }
        }
        if (!isOpen) {
            setIsUploading(false);
        }
    }, [isOpen, defaultObject]);
    
    const handleSave = useCallback(async () => {
        let successful = true;
        // Upload the images
        if (uploadedPrimaryFile || uploadedSecondaryFile) {
            setIsUploading(true)
            const uploadImagesPromises = [];
            if (uploadedPrimaryFile) {
                uploadImagesPromises.push(Api.uploadReportCustomLogo(CUSTOM_PRIMARY_LOGO_NAME, uploadedPrimaryFile));
            }
            if (uploadedSecondaryFile) {
                uploadImagesPromises.push(Api.uploadReportCustomLogo(CUSTOM_SECONDARY_LOGO_NAME, uploadedSecondaryFile));
            }
            await Promise.all(uploadImagesPromises)
                // .then(res => {})
                .catch(e => {
                    const errorMessage = getErrorMessage(e);
                    alert(errorMessage);
                    successful = false;
                })
                .finally(() => {
                    setIsUploading(false);
                });
        }
        if (successful) {
            onSuccess(editorObject);
        }
    }, [onSuccess, editorObject, uploadedPrimaryFile, uploadedSecondaryFile, getErrorMessage]);
    

    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={480}
            height={500}
            title={t('SMC_MESSEGE_TITLE')}
        >
            <SMCModalBody>
                <RowLayout>
                    <SMCLabel width={130}>{t('SMC_CUSTOM_DETAILS_COMPANY_TEXT')}</SMCLabel>
                    <SMCLabel>&copy; {new Date().getFullYear()}</SMCLabel>
                    <SMCTextInput value={customText} onChange={e => setPageValue(customDetailsSchema.customText, e.target.value)} />
                </RowLayout>

                <Sepperator />
                
                <RowLayout height={60}>
                    <SMCCheckbox
                        checked={enablePrimaryLogo}
                        onChange={e => setPageValue(customDetailsSchema.enablePrimaryLogo, e.target.checked)}
                    />
                    <SMCLabel inline width={110} style={{ opacity: enablePrimaryLogo ? '1.0' : '0.5' }}>{t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO')}</SMCLabel>
                    <span style={{ width: 145, marginRight:'10px'}}>
                        <img height={29} width={136} src={primaryLogoSrc} style={{ opacity: enablePrimaryLogo ? '1' : '0.4' }} alt="Primary Logo" />
                    </span>
                    <SMCButton disabled={!enablePrimaryLogo} onClick={() => document.getElementById('primaryFileInput').click()}>{t('SMC_CUSTOM_DETAILS_UPLOAD')}</SMCButton>
                    <input type="file" accept="image/png" id="primaryFileInput" onChange={onUploadPrimaryFile} style={{ display: 'none' }} />
                    <Tooltip
                        title={
                            <>
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP1')}<br />
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP2')}
                            <strong>{t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP3', [29, 136])}</strong>
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP4')}
                            </>
                        }
                    >
                        <img style={{ paddingLeft: '4px' }} src={tooltipIcon} alt="help" />
                    </Tooltip>
                </RowLayout>
                
                <Sepperator />
                
                <RowLayout height={60}>
                    <SMCCheckbox
                        checked={enableSecondaryLogo}
                        onChange={e => setPageValue(customDetailsSchema.enableSecondaryLogo, e.target.checked)}
                    />
                    <SMCLabel inline width={110} style={{ opacity: enableSecondaryLogo ? '1.0' : '0.5' }}>{t('SMC_CUSTOM_DETAILS_SECONDARY_LOGO')}</SMCLabel>
                    <span style={{ width: 145, marginRight:'10px' }}>
                        <img height={25} width={secondaryLogoWidth} src={secondaryLogoSrc} style={{ opacity: enableSecondaryLogo ? '1.0' : '0.4' }} alt="Secondary Logo" />
                    </span>
                    <SMCButton disabled={!enableSecondaryLogo} onClick={() => document.getElementById('secondaryFileInput').click()}>{t('SMC_CUSTOM_DETAILS_UPLOAD')}</SMCButton>
                    <input type="file" accept="image/png" id="secondaryFileInput" onChange={onUploadSecondaryFile} style={{ display: 'none' }} />
                    <Tooltip
                        title={
                            <>
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP1')}<br />
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP2')}
                            <strong>{t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP3', [25, 145])}</strong>
                            {t('SMC_CUSTOM_DETAILS_PRIMARY_LOGO_TOOLTIP4')}
                            </>
                        }
                    >
                        <img style={{ paddingLeft: '4px' }} src={tooltipIcon} alt="help" />
                    </Tooltip>
                </RowLayout>
            </SMCModalBody>
            <SMCModalButtonContainer>
                {/* Loading spinner */}
                <div className="lds-dual-ring" style={{ visibility: isUploading ? 'visible' : 'hidden', height: '22px' }} />
                <SMCButton onClick={handleSave}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_SAVE')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
};

export default CompanyDetailsEditorModal;